import React from 'react';
import DocsLayout from './DocsLayout';
import { 
  Heading, Paragraph, CodeBlock, InlineCode, Callout, Table, List, 
  Steps, Step, Card, CardGrid, Tabs, Badge 
} from './MDXComponents';
import { 
  Book, Shield, Server, Database, GitBranch, Zap, Settings, 
  Users, Key, Globe, AlertTriangle, Terminal, FileText, Layers, Lock, ArrowRight
} from 'lucide-react';

// ============================================================================
// DOCUMENTATION CONTENT
// ============================================================================

const GettingStartedContent = () => (
  <>
    <Paragraph>
      Welcome to the VISION Knowledge Base. This documentation covers the architecture, 
      deployment, and troubleshooting of the VISION dashboard application.
    </Paragraph>

    <Heading level={2}>Quick Start</Heading>
    
    <Steps>
      <Step title="Clone the repository">
        Clone the VISION dashboard from GitLab and install dependencies.
      </Step>
      <Step title="Configure environment">
        Set up your Okta credentials and AWS configuration.
      </Step>
      <Step title="Run locally">
        Start the development server with <InlineCode>npm start</InlineCode>
      </Step>
      <Step title="Deploy">
        Use the deploy script to push to your target environment.
      </Step>
    </Steps>

    <CodeBlock 
      filename="Terminal"
      language="bash"
      code={`# Clone and install
git clone https://gitlab.com/dish-saas/scm/vision/visiondashboard.git
cd visiondashboard
npm install

# Run development server
npm start

# Deploy to an environment
./deploy.sh`}
    />

    <Callout type="info" title="Prerequisites">
      You need Node.js 18+, AWS CLI configured, and membership in an App-VISION-* Okta group.
    </Callout>
  </>
);

const ArchitectureContent = () => (
  <>
    <Paragraph>
      VISION is a React SPA hosted on CloudFront with S3 origin. The backend consists of 
      API Gateway with Lambda functions and DynamoDB for data storage.
    </Paragraph>

    <Heading level={2}>System Components</Heading>

    <CardGrid>
      <Card title="Frontend" icon={Globe}>
        React SPA with Tailwind CSS, hosted on CloudFront with S3 origin. 
        Uses Okta PKCE flow for authentication.
      </Card>
      <Card title="API Gateway" icon={Server}>
        REST API with Lambda authorizer for JWT validation. 
        WebSocket API for real-time ITOps Advisor chat.
      </Card>
      <Card title="Lambda Functions" icon={Zap}>
        Node.js 18 runtime. Includes authorizer, API handlers, 
        and Bedrock integration for AI features.
      </Card>
      <Card title="Data Layer" icon={Database}>
        DynamoDB tables for entities, changes, and chat sessions. 
        S3 for knowledge base documents.
      </Card>
    </CardGrid>

    <Heading level={2}>Data Flow</Heading>

    <List items={[
      'User authenticates via Okta PKCE flow',
      'Frontend receives JWT tokens (access + ID)',
      'API calls include Authorization: Bearer <token>',
      'API Gateway invokes Lambda authorizer',
      'Authorizer validates JWT and checks groups',
      'Backend Lambda processes request',
      'Response returned to frontend'
    ]} ordered />
  </>
);

const AuthenticationContent = () => (
  <>
    <Paragraph>
      VISION uses Okta for authentication with OAuth 2.0 Authorization Code flow with PKCE. 
      This is the recommended approach for Single Page Applications.
    </Paragraph>

    <Heading level={2}>Authentication Flow</Heading>

    <Steps>
      <Step title="User opens app">
        Frontend checks localStorage for valid tokens
      </Step>
      <Step title="Redirect to Okta">
        If no valid token, redirect to Okta with PKCE challenge
      </Step>
      <Step title="User authenticates">
        User enters credentials at dish.okta.com
      </Step>
      <Step title="Token exchange">
        Frontend exchanges auth code for tokens using PKCE verifier
      </Step>
      <Step title="API calls">
        Access token included in Authorization header
      </Step>
      <Step title="Lambda validation">
        Authorizer validates JWT signature and group membership
      </Step>
    </Steps>

    <Heading level={2}>Environment Configuration</Heading>

    <Table 
      headers={['Environment', 'Client ID', 'Redirect URI']}
      rows={[
        ['visiondev', '0oa18bph4bbwxh1Gw2p8', 'https://visiondev.echostar.com/callback'],
        ['visiontest', '0oa18bpkei2tFfH7i2p8', 'https://visiontest.echostar.com/callback'],
        ['visionint', '0oa18899ekpA91F1P2p8', 'https://visionint.echostar.com/callback'],
        ['vision (prod)', '0oa188982x9qH5lzH2p8', 'https://vision.echostar.com/callback'],
      ]}
    />

    <Callout type="success" title="Auto-Detection">
      The frontend automatically detects the environment from the hostname and uses 
      the correct Okta configuration. No build-time configuration needed.
    </Callout>
  </>
);

const GroupsContent = () => (
  <>
    <Paragraph>
      Access to VISION is controlled via Okta group membership. Users must be a member 
      of at least one VISION group to access the application.
    </Paragraph>

    <Heading level={2}>Admin Groups</Heading>
    <Paragraph>Full access including admin features:</Paragraph>
    <List items={[
      'App-VISION-Admin-Dev',
      'App-VISION-Admin-Test', 
      'App-VISION-Admin-Int',
      'App-VISION-Admin-Prod'
    ]} />

    <Heading level={2}>User Groups</Heading>
    <Paragraph>Standard user access (no admin features):</Paragraph>
    <List items={[
      'App-VISION-User-Dev',
      'App-VISION-User-Test',
      'App-VISION-User-Int', 
      'App-VISION-User-Prod'
    ]} />

    <Callout type="warning" title="Security Note">
      Client-side group checks are for UI display only. All actual authorization 
      is enforced server-side by the Lambda authorizer.
    </Callout>
  </>
);

const LambdaAuthorizerContent = () => (
  <>
    <Paragraph>
      The <InlineCode>vision-api-authorizer</InlineCode> Lambda validates all API requests 
      by checking JWT signatures and group membership.
    </Paragraph>

    <Heading level={2}>Validation Steps</Heading>
    <List items={[
      'Extract JWT from Authorization header',
      'Fetch Okta public keys from JWKS endpoint',
      'Verify JWT signature using RS256',
      'Validate token expiration, issuer, and audience',
      'Extract groups claim and check membership',
      'Return IAM policy (Allow/Deny) to API Gateway'
    ]} ordered />

    <Heading level={2}>Environment Variables</Heading>
    <CodeBlock 
      filename="Lambda Configuration"
      code={`OKTA_ISSUER=https://dish.okta.com/oauth2/aus18cezfnrMzbgp32p8
OKTA_CLIENT_ID=<environment-specific-client-id>`}
    />

    <Heading level={2}>Deployment</Heading>
    <CodeBlock 
      filename="Terminal"
      language="bash"
      code={`# Update Lambda code
cd infrastructure/authorizer
zip -r ../authorizer.zip index.js node_modules/
aws lambda update-function-code \\
  --function-name vision-api-authorizer \\
  --zip-file fileb://../authorizer.zip \\
  --region us-west-2`}
    />
  </>
);


const DeploymentContent = () => (
  <>
    <Paragraph>
      VISION uses an interactive deployment script that supports multiple environments. 
      The script handles building, S3 sync, and CloudFront invalidation.
    </Paragraph>

    <Heading level={2}>Environments</Heading>
    <Table 
      headers={['Environment', 'CNAME', 'Description']}
      rows={[
        ['visiondev', 'visiondev.echostar.com', 'Development environment'],
        ['visiontest', 'visiontest.echostar.com', 'Test environment'],
        ['visionint', 'visionint.echostar.com', 'Integration environment'],
        ['vision', 'vision.echostar.com', 'Production environment'],
      ]}
    />

    <Heading level={2}>Deploy Script Usage</Heading>
    <CodeBlock 
      filename="Terminal"
      language="bash"
      code={`# Interactive mode
./deploy.sh

# Direct deployment
./deploy.sh --dev    # Deploy to visiondev
./deploy.sh --test   # Deploy to visiontest
./deploy.sh --int    # Deploy to visionint
./deploy.sh --prod   # Deploy to vision (production)`}
    />

    <Callout type="warning" title="Production Deployment">
      Production deployments require additional confirmation. Always verify changes 
      in lower environments before deploying to production.
    </Callout>

    <Heading level={2}>Lambda Authorizer Deployment</Heading>
    <Paragraph>
      The deploy script can optionally deploy the Lambda authorizer. When prompted, 
      select "y" to update the authorizer with the correct Okta configuration.
    </Paragraph>
  </>
);

const APIEndpointsContent = () => (
  <>
    <Paragraph>
      VISION exposes REST and WebSocket APIs through API Gateway. All endpoints 
      require authentication via the Lambda authorizer.
    </Paragraph>

    <Heading level={2}>REST Endpoints</Heading>
    <Table 
      headers={['Method', 'Endpoint', 'Description']}
      rows={[
        ['GET', '/entities', 'List all entities'],
        ['GET', '/entities/{id}', 'Get entity details'],
        ['GET', '/changes', 'List change requests'],
        ['GET', '/changes/{id}', 'Get change details'],
        ['POST', '/feedback', 'Submit feedback'],
        ['GET', '/admin/stats', 'Admin statistics (admin only)'],
      ]}
    />

    <Heading level={2}>WebSocket API</Heading>
    <Paragraph>
      The ITOps Advisor uses WebSocket for real-time chat with Bedrock AI.
    </Paragraph>
    <CodeBlock 
      filename="WebSocket Connection"
      language="javascript"
      code={`const ws = new WebSocket('wss://api.visiondev.echostar.com/ws');

ws.onopen = () => {
  ws.send(JSON.stringify({
    action: 'sendMessage',
    message: 'How do I troubleshoot a failed deployment?'
  }));
};

ws.onmessage = (event) => {
  const response = JSON.parse(event.data);
  console.log('AI Response:', response.message);
};`}
    />
  </>
);

const TroubleshootingContent = () => (
  <>
    <Heading level={2}>Common Issues</Heading>

    <Heading level={3}>"Unauthorized" Error</Heading>
    <List items={[
      'Token may be expired - log out and log back in',
      'Check OKTA_CLIENT_ID environment variable in Lambda',
      'Verify OKTA_ISSUER matches the custom auth server'
    ]} />

    <Heading level={3}>"No access group" Error</Heading>
    <List items={[
      'User is not a member of App-VISION-Admin-* or App-VISION-User-* groups',
      'Groups claim may not be included in token - check auth server config',
      'Group name mismatch (check for special characters)'
    ]} />

    <Heading level={3}>Admin Menu Not Showing</Heading>
    <List items={[
      'User may not be in an Admin group (only User group)',
      'Try logging out and back in to get a fresh token',
      'Check browser console for [isAdmin] debug messages'
    ]} />

    <Heading level={2}>Debug Commands</Heading>
    <CodeBlock 
      filename="Terminal"
      language="bash"
      code={`# View Lambda authorizer logs
aws logs tail /aws/lambda/vision-api-authorizer --follow --region us-west-2

# Check Lambda configuration
aws lambda get-function-configuration \\
  --function-name vision-api-authorizer \\
  --region us-west-2

# Test token (browser console)
console.log(localStorage.getItem('okta_id_token'));`}
    />
  </>
);

const DynamoDBContent = () => (
  <>
    <Paragraph>
      VISION uses DynamoDB for storing entities, changes, and session data. 
      Tables use single-table design patterns where appropriate.
    </Paragraph>

    <Heading level={2}>Tables</Heading>
    <Table 
      headers={['Table', 'Partition Key', 'Sort Key', 'Purpose']}
      rows={[
        ['vision-entities', 'PK (entity_id)', 'SK (type)', 'Entity data and relationships'],
        ['vision-changes', 'change_id', 'timestamp', 'Change request tracking'],
        ['vision-sessions', 'session_id', 'created_at', 'ITOps Advisor chat sessions'],
        ['vision-feedback', 'feedback_id', 'timestamp', 'User feedback storage'],
      ]}
    />

    <Heading level={2}>Access Patterns</Heading>
    <List items={[
      'Get entity by ID: Query PK = entity_id',
      'List entities by type: Query GSI type-index',
      'Get changes by date range: Query with timestamp filter',
      'Get user sessions: Query GSI user-index'
    ]} />

    <Callout type="info" title="TTL">
      Session data has a 30-day TTL. Feedback data is retained indefinitely.
    </Callout>
  </>
);

const BedrockContent = () => (
  <>
    <Paragraph>
      The ITOps Advisor uses Amazon Bedrock with Claude for AI-powered incident 
      troubleshooting and recommendations.
    </Paragraph>

    <Heading level={2}>Configuration</Heading>
    <CodeBlock 
      filename="Lambda Environment"
      code={`BEDROCK_MODEL_ID=anthropic.claude-3-sonnet-20240229-v1:0
BEDROCK_REGION=us-west-2
KB_BUCKET=vision-knowledge-base`}
    />

    <Heading level={2}>Knowledge Base Integration</Heading>
    <Paragraph>
      The AI uses RAG (Retrieval Augmented Generation) with documents uploaded 
      to the Knowledge Base. Supported formats:
    </Paragraph>
    <List items={[
      'PDF documents',
      'Word documents (.docx)',
      'Plain text files (.txt)',
      'Markdown files (.md)'
    ]} />

    <Callout type="success" title="Context Window">
      Claude 3 Sonnet supports up to 200K tokens, allowing for comprehensive 
      context from multiple KB documents.
    </Callout>
  </>
);

// ============================================================================
// AUTH DOCS CONTENT
// ============================================================================

const AuthOverviewContent = () => (
  <>
    <Paragraph>
      VISION uses Okta for authentication with OAuth 2.0 Authorization Code flow with PKCE 
      (Proof Key for Code Exchange). This is the recommended flow for Single Page Applications.
    </Paragraph>

    <CardGrid>
      <Card title="Auth Server" icon={Server}>
        Custom "Vision" auth server on dish.okta.com
      </Card>
      <Card title="Flow Type" icon={Key}>
        Authorization Code with PKCE (no client secret)
      </Card>
      <Card title="Token Storage" icon={Database}>
        Browser localStorage for tokens
      </Card>
    </CardGrid>
  </>
);

const AuthFlowContent = () => (
  <>
    <Paragraph>
      The authentication flow ensures secure access to VISION using industry-standard OAuth 2.0 with PKCE.
    </Paragraph>

    <Steps>
      <Step title="User Opens App">
        Frontend checks localStorage for existing valid tokens
      </Step>
      <Step title="Redirect to Okta">
        If no valid token, user is redirected to Okta login with PKCE challenge
      </Step>
      <Step title="User Authenticates">
        User enters credentials at dish.okta.com, Okta validates and returns authorization code
      </Step>
      <Step title="Token Exchange">
        Frontend exchanges authorization code for tokens using PKCE code_verifier (no client secret needed)
      </Step>
      <Step title="API Calls">
        Frontend includes access_token in Authorization header for all API requests
      </Step>
      <Step title="Lambda Authorizer">
        API Gateway invokes vision-api-authorizer Lambda to validate JWT and check group membership
      </Step>
    </Steps>
  </>
);

const AuthEnvironmentsContent = () => (
  <>
    <Paragraph>
      Each environment has its own Okta SPA client with environment-specific redirect URIs. 
      The custom auth server is shared across all environments.
    </Paragraph>

    <Table 
      headers={['Environment', 'Client ID', 'Redirect URI']}
      rows={[
        ['visiondev', '0oa18bph4bbwxh1Gw2p8', 'https://visiondev.echostar.com/callback'],
        ['visiontest', '0oa18bpkei2tFfH7i2p8', 'https://visiontest.echostar.com/callback'],
        ['visionint', '0oa18899ekpA91F1P2p8', 'https://visionint.echostar.com/callback'],
        ['vision (prod)', '0oa188982x9qH5lzH2p8', 'https://vision.echostar.com/callback'],
      ]}
    />

    <Callout type="success" title="Auto-Detection">
      The frontend automatically detects the environment from the hostname and uses 
      the correct Okta configuration. No build-time configuration needed.
    </Callout>
  </>
);

const AuthGroupsPermissionsContent = () => (
  <>
    <Paragraph>
      Access is controlled via Okta group membership. Users must be a member of at least 
      one VISION group to access the application.
    </Paragraph>

    <Heading level={2}>Admin Groups</Heading>
    <Paragraph>Full access including admin features:</Paragraph>
    <List items={[
      'App-VISION-Admin-Dev',
      'App-VISION-Admin-Test',
      'App-VISION-Admin-Int',
      'App-VISION-Admin-Prod'
    ]} />

    <Heading level={2}>User Groups</Heading>
    <Paragraph>Standard user access (no admin features):</Paragraph>
    <List items={[
      'App-VISION-User-Dev',
      'App-VISION-User-Test',
      'App-VISION-User-Int',
      'App-VISION-User-Prod'
    ]} />

    <Callout type="warning" title="Security Note">
      Client-side group checks (like showing/hiding admin menu) are for UI display only. 
      All actual authorization is enforced server-side by the Lambda authorizer.
    </Callout>
  </>
);

const AuthLambdaAuthorizerContent = () => (
  <>
    <Paragraph>
      The <InlineCode>vision-api-authorizer</InlineCode> Lambda function validates all API requests 
      by checking JWT signatures and group membership.
    </Paragraph>

    <Heading level={2}>Validation Steps</Heading>
    <List items={[
      'Extracts JWT from Authorization header',
      'Fetches Okta public keys from JWKS endpoint',
      'Verifies JWT signature using RS256 algorithm',
      'Validates token expiration, issuer, and audience',
      'Extracts groups claim and checks membership',
      'Returns IAM policy (Allow/Deny) to API Gateway'
    ]} ordered />

    <Heading level={2}>Lambda Environment Variables</Heading>
    <CodeBlock 
      filename="Lambda Configuration"
      code={`OKTA_ISSUER=https://dish.okta.com/oauth2/aus18cezfnrMzbgp32p8
OKTA_CLIENT_ID=<environment-specific-client-id>`}
    />
  </>
);

const AuthOktaConfigContent = () => (
  <>
    <Heading level={2}>Custom Auth Server: "Vision"</Heading>
    <Table 
      headers={['Property', 'Value']}
      rows={[
        ['Issuer URL', 'https://dish.okta.com/oauth2/aus18cezfnrMzbgp32p8'],
        ['JWKS URI', '.../.well-known/jwks.json'],
      ]}
    />

    <Heading level={2}>SPA Client Settings</Heading>
    <Table 
      headers={['Property', 'Value']}
      rows={[
        ['Grant Type', 'Authorization Code with PKCE'],
        ['Client Secret', 'None (PKCE doesn\'t require it)'],
        ['Token Endpoint Auth', 'None'],
      ]}
    />

    <Heading level={2}>Groups Claim Configuration</Heading>
    <Table 
      headers={['Property', 'Value']}
      rows={[
        ['Claim Name', 'groups'],
        ['Include in', 'ID Token and Access Token'],
        ['Filter', 'App-VISION*'],
      ]}
    />

    <Heading level={2}>Requested Scopes</Heading>
    <List items={['openid', 'profile', 'email', 'groups']} />
  </>
);

const AuthTroubleshootingContent = () => (
  <>
    <Heading level={2}>"Unauthorized" Error</Heading>
    <List items={[
      'Token may be expired - log out and log back in',
      'Check OKTA_CLIENT_ID environment variable in Lambda',
      'Verify OKTA_ISSUER matches the custom auth server'
    ]} />

    <Heading level={2}>"No access group" Error</Heading>
    <List items={[
      'User is not a member of App-VISION-Admin-* or App-VISION-User-* groups',
      'Groups claim may not be included in token - check auth server config',
      'Group name mismatch (check for special characters like en-dash vs hyphen)'
    ]} />

    <Heading level={2}>Admin Menu Not Showing</Heading>
    <List items={[
      'User may not be in an Admin group (only User group)',
      'Try logging out and back in to get a fresh token',
      'Check browser console for [isAdmin] debug messages'
    ]} />

    <Heading level={2}>Redirect Loop</Heading>
    <List items={[
      'Redirect URI in Okta may not match the environment',
      'Clear browser localStorage and try again',
      'Check that the callback route is properly configured'
    ]} />

    <Heading level={2}>Debug Commands</Heading>
    <CodeBlock 
      filename="Terminal"
      language="bash"
      code={`# View Lambda authorizer logs
aws logs tail /aws/lambda/vision-api-authorizer --follow --region us-west-2

# Check Lambda configuration
aws lambda get-function-configuration \\
  --function-name vision-api-authorizer \\
  --region us-west-2

# Test token validation (from browser console)
console.log(localStorage.getItem('okta_id_token'));`}
    />
  </>
);

const AuthKeyFilesContent = () => (
  <>
    <Paragraph>
      Key files and resources for the authentication system.
    </Paragraph>

    <Table 
      headers={['File', 'Path', 'Purpose']}
      rows={[
        ['Frontend Auth', 'src/utils/customOktaAuth.js', 'PKCE flow implementation'],
        ['Environment Config', 'src/utils/secretsManager.js', 'Auto-detects environment'],
        ['Lambda Authorizer', 'infrastructure/authorizer/index.js', 'JWT validation'],
        ['Deploy Script', 'deploy.sh', 'Lambda deployment option'],
      ]}
    />
  </>
);

const IAMRolesContent = () => (
  <>
    <Paragraph>
      All VISION Lambda functions use a shared IAM role in the dev environment. 
      Upper environments should use separate, least-privilege roles.
    </Paragraph>

    <Heading level={2}>Lambda Execution Role</Heading>
    <Paragraph>
      Dev environment role used by all Lambda functions:
    </Paragraph>
    <CodeBlock 
      filename="IAM Role ARN"
      code={`arn:aws:iam::109903591893:role/admin-servicenow-LambdaDynamoDBAccess-dev-us-west-2`}
    />

    <Heading level={2}>Attached Policies</Heading>
    <Table 
      headers={['Policy Name', 'Type', 'Purpose']}
      rows={[
        ['adminservicenowLambdaDynamoDBAccessdevPolicy', 'Custom', 'DynamoDB CRUD + Bedrock invoke'],
        ['invoke-bedrock-dev', 'Custom', 'Full Bedrock access, S3, OpenSearch Serverless'],
        ['Manage-APIKeys-Secrets-dev', 'Custom', 'Secrets Manager access for API keys'],
        ['monitoring-cloudwatch-dev', 'Custom', 'CloudWatch metrics and logs'],
        ['schedule-eventbridge-dev', 'Custom', 'EventBridge scheduling'],
        ['CloudWatchReadOnlyAccess', 'AWS Managed', 'Read CloudWatch metrics'],
        ['AmazonBedrockLimitedAccess', 'AWS Managed', 'Bedrock model access'],
      ]}
    />

    <Callout type="warning" title="Dev Environment Note">
      The dev environment uses broad permissions for flexibility during development. 
      Production environments should use least-privilege policies scoped to specific resources.
    </Callout>

    <Heading level={2}>DynamoDB Policy</Heading>
    <CodeBlock 
      filename="adminservicenowLambdaDynamoDBAccessdevPolicy"
      language="json"
      code={`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "dynamodb:GetItem",
        "dynamodb:PutItem",
        "dynamodb:UpdateItem",
        "dynamodb:DeleteItem",
        "dynamodb:Query",
        "dynamodb:Scan",
        "dynamodb:BatchGetItem",
        "dynamodb:BatchWriteItem"
      ],
      "Resource": "arn:aws:dynamodb:us-west-2:109903591893:table/*"
    },
    {
      "Effect": "Allow",
      "Action": [
        "bedrock:InvokeModel",
        "bedrock:Retrieve",
        "bedrock:RetrieveAndGenerate",
        "lambda:InvokeFunction"
      ],
      "Resource": "*"
    }
  ]
}`}
    />

    <Heading level={2}>Bedrock Policy</Heading>
    <CodeBlock 
      filename="invoke-bedrock-dev"
      language="json"
      code={`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "bedrock:InvokeModel",
        "bedrock:InvokeModelWithResponseStream",
        "bedrock:InvokeAgent",
        "bedrock:Retrieve",
        "bedrock:Get*",
        "bedrock:List*",
        "s3:GetObject",
        "s3:PutObject",
        "s3:ListBucket",
        "aoss:APIAccessAll"
      ],
      "Resource": "*"
    }
  ]
}`}
    />
  </>
);

const LambdaFunctionsContent = () => (
  <>
    <Paragraph>
      VISION uses multiple Lambda functions for different purposes. All functions 
      in dev share the same IAM role.
    </Paragraph>

    <Heading level={2}>Active Lambda Functions</Heading>
    <Table 
      headers={['Function Name', 'Runtime', 'Purpose']}
      rows={[
        ['vision-api-authorizer', 'Node.js 18', 'JWT validation for API Gateway'],
        ['vision-dashboard-api', 'Node.js 22', 'Main dashboard API'],
        ['vision-dashboard-dev-bedrock', 'Node.js 18', 'Bedrock AI integration'],
        ['vision-dashboard-dev-health', 'Node.js 18', 'Health check endpoint'],
        ['vision-change-management-api', 'Python 3.11', 'Change management operations'],
        ['vision-fetch-change-cis', 'Python 3.11', 'Fetch CIs for changes'],
        ['vision-ci-entity-matcher', 'Python 3.11', 'CI to entity matching'],
        ['vision-scr-crud-function', 'Python 3.12', 'SCR CRUD operations'],
        ['vision-itops-snow-chg-review-lambda-dev', 'Python 3.13', 'ServiceNow CHG review'],
      ]}
    />

    <Callout type="info" title="Sandbox Note">
      Some functions like <code>Midone-vision-okta-lambda</code> and <code>vision_review_servicenow_integration</code> are 
      legacy/experimental from dev sandbox testing and may not be actively used.
    </Callout>

    <Heading level={2}>API Gateways</Heading>
    <Table 
      headers={['API ID', 'Name', 'Purpose']}
      rows={[
        ['9sn4n21ry0', 'vision-scr-crud-gateway', 'SCR CRUD operations (dev)'],
        ['f94pscyhmh', 'vision-dashboard-dev-api', 'Dashboard API (dev)'],
        ['eljjzbjnw4', 'vision-dashboard-prod-api', 'Dashboard API (prod)'],
      ]}
    />
  </>
);

const DynamoDBTablesContent = () => (
  <>
    <Paragraph>
      VISION uses DynamoDB for data storage. The dev environment contains some 
      legacy/experimental tables from sandbox testing.
    </Paragraph>

    <Heading level={2}>Active Tables</Heading>
    <Table 
      headers={['Table Name', 'Purpose', 'Status']}
      rows={[
        ['vision-entity-mappings', 'CI to entity mappings', 'Active'],
        ['vision-ci-entity-matches', 'Entity match results', 'Active'],
        ['vision-mapping-feedback', 'User feedback on mappings', 'Active'],
        ['VisionReviewServiceNowLogs', 'ServiceNow integration logs', 'Active'],
      ]}
    />

    <Heading level={2}>Legacy/Sandbox Tables</Heading>
    <Callout type="warning" title="Sandbox Tables">
      The following tables were created during development/testing and may not be 
      actively used. Review before cleanup.
    </Callout>
    <Table 
      headers={['Table Name', 'Original Purpose', 'Status']}
      rows={[
        ['vision-dashboard-prod-access-requests', 'Access request tracking', 'Legacy - Review'],
        ['vision-dashboard-prod-change-requests', 'Change request storage', 'Legacy - Review'],
        ['vision-dashboard-prod-compliance-reports', 'Compliance reporting', 'Legacy - Review'],
        ['vision-dashboard-prod-incidents', 'Incident tracking', 'Legacy - Review'],
        ['vision-dashboard-prod-knowledge-base', 'KB document metadata', 'Legacy - Review'],
        ['vision-dashboard-prod-prompts', 'AI prompt templates', 'Legacy - Review'],
        ['vision-dashboard-prod-service-health', 'Service health data', 'Legacy - Review'],
        ['vision-dashboard-prod-system-status', 'System status', 'Legacy - Review'],
        ['vision-dashboard-prod-token-usage', 'Token usage tracking', 'Legacy - Review'],
        ['vision-dashboard-prod-users', 'User data', 'Legacy - Review'],
      ]}
    />

    <Callout type="info" title="Naming Convention">
      Tables named "prod" in the dev account are from early development when naming 
      conventions weren't established. They don't contain production data.
    </Callout>
  </>
);

// ============================================================================
// SERVICENOW CHANGE MANAGEMENT PIPELINE DOCUMENTATION
// ============================================================================

// Pipeline Flow Diagram Component
const PipelineFlowDiagram = () => (
  <div className="my-8 p-6 bg-gray-900/50 rounded-xl border border-gray-700/50">
    {/* Top Row - Steps 1, 2, 3 */}
    <div className="flex items-center justify-between mb-4">
      {/* Step 1 */}
      <div className="flex flex-col items-center">
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/50 rounded-lg p-4 text-center min-w-[140px] shadow-lg shadow-blue-500/10">
          <div className="text-blue-400 font-semibold text-sm mb-1">Step 1</div>
          <div className="text-white font-medium">Changes Fetcher</div>
        </div>
        <div className="h-8 w-0.5 bg-gradient-to-b from-blue-500/50 to-blue-500/20 my-2"></div>
        <div className="bg-gray-800/80 border border-gray-600/50 rounded-md px-3 py-2 text-xs text-gray-300">
          DynamoDB Table
        </div>
      </div>

      {/* Arrow 1→2 */}
      <div className="flex items-center px-2 -mt-16">
        <div className="w-8 h-0.5 bg-gradient-to-r from-blue-500/50 to-purple-500/50"></div>
        <div className="text-purple-400">→</div>
      </div>

      {/* Step 2 */}
      <div className="flex flex-col items-center">
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/50 rounded-lg p-4 text-center min-w-[140px] shadow-lg shadow-purple-500/10">
          <div className="text-purple-400 font-semibold text-sm mb-1">Step 2</div>
          <div className="text-white font-medium">Entity Finder</div>
          <div className="text-purple-300/70 text-xs mt-1">(AI-Powered)</div>
        </div>
        <div className="h-8 w-0.5 bg-gradient-to-b from-purple-500/50 to-purple-500/20 my-2"></div>
        <div className="bg-gray-800/80 border border-gray-600/50 rounded-md px-3 py-2 text-xs text-gray-300">
          Bedrock KB Matching
        </div>
      </div>

      {/* Arrow 2→3 */}
      <div className="flex items-center px-2 -mt-16">
        <div className="w-8 h-0.5 bg-gradient-to-r from-purple-500/50 to-cyan-500/50"></div>
        <div className="text-cyan-400">→</div>
      </div>

      {/* Step 3 */}
      <div className="flex flex-col items-center">
        <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 border border-cyan-500/50 rounded-lg p-4 text-center min-w-[140px] shadow-lg shadow-cyan-500/10">
          <div className="text-cyan-400 font-semibold text-sm mb-1">Step 3</div>
          <div className="text-white font-medium">Relationships</div>
        </div>
        <div className="h-8 w-0.5 bg-gradient-to-b from-cyan-500/50 to-cyan-500/20 my-2"></div>
        <div className="bg-gray-800/80 border border-gray-600/50 rounded-md px-3 py-2 text-xs text-gray-300">
          Dynatrace API
        </div>
      </div>
    </div>

    {/* Connecting Line from Step 3 to Step 4 */}
    <div className="flex justify-end pr-[70px] mb-4">
      <div className="flex flex-col items-center">
        <div className="h-6 w-0.5 bg-gradient-to-b from-cyan-500/30 to-green-500/30"></div>
        <div className="text-green-400 rotate-90">→</div>
      </div>
    </div>

    {/* Bottom Row - Steps 4, 5 */}
    <div className="flex items-center justify-end gap-4">
      {/* Step 4 */}
      <div className="flex flex-col items-center">
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-500/50 rounded-lg p-4 text-center min-w-[140px] shadow-lg shadow-green-500/10">
          <div className="text-green-400 font-semibold text-sm mb-1">Step 4</div>
          <div className="text-white font-medium">Health Check</div>
        </div>
        <div className="h-8 w-0.5 bg-gradient-to-b from-green-500/50 to-green-500/20 my-2"></div>
        <div className="bg-gray-800/80 border border-gray-600/50 rounded-md px-3 py-2 text-xs text-gray-300">
          Dynatrace Metrics
        </div>
      </div>

      {/* Arrow 4→5 */}
      <div className="flex items-center px-2 -mt-16">
        <div className="w-8 h-0.5 bg-gradient-to-r from-green-500/50 to-orange-500/50"></div>
        <div className="text-orange-400">→</div>
      </div>

      {/* Step 5 */}
      <div className="flex flex-col items-center">
        <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-500/50 rounded-lg p-4 text-center min-w-[140px] shadow-lg shadow-orange-500/10">
          <div className="text-orange-400 font-semibold text-sm mb-1">Step 5</div>
          <div className="text-white font-medium">Visualization</div>
        </div>
        <div className="h-8 w-0.5 bg-gradient-to-b from-orange-500/50 to-orange-500/20 my-2"></div>
        <div className="bg-gray-800/80 border border-gray-600/50 rounded-md px-3 py-2 text-xs text-gray-300">
          S3 + DynamoDB
        </div>
      </div>
    </div>

    {/* Legend */}
    <div className="mt-6 pt-4 border-t border-gray-700/50 flex items-center justify-center gap-6 text-xs text-gray-400">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-gradient-to-br from-blue-500/40 to-purple-500/40 border border-blue-500/50"></div>
        <span>Lambda Function</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-gray-800/80 border border-gray-600/50"></div>
        <span>Data Store / API</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-cyan-400">→</span>
        <span>Data Flow</span>
      </div>
    </div>
  </div>
);

const SCMPipelineOverviewContent = () => (
  <>
    <Paragraph>
      The ServiceNow Change Management Pipeline is a 5-step automated system that enriches 
      ServiceNow change requests with Dynatrace observability data, creating comprehensive 
      change impact analysis with health monitoring and visualization.
    </Paragraph>

    <Heading level={2}>Pipeline Architecture</Heading>
    <PipelineFlowDiagram />

    <Heading level={2}>Key Technologies</Heading>
    <CardGrid>
      <Card title="AWS Lambda" icon={Zap}>
        Python 3.11/3.12 runtime for all pipeline steps
      </Card>
      <Card title="DynamoDB" icon={Database}>
        Single table design: v-scr-ui-changes
      </Card>
      <Card title="AWS Bedrock" icon={Server}>
        AI-powered entity matching via Knowledge Base
      </Card>
      <Card title="S3 Storage" icon={Database}>
        Compressed visualization storage
      </Card>
    </CardGrid>

    <Heading level={2}>Lambda Functions</Heading>
    <Table 
      headers={['Function', 'Runtime', 'Memory', 'Timeout', 'Purpose']}
      rows={[
        ['single-table-step1-changes', 'Python 3.11', '416 MB', '5 min', 'Fetch changes from ServiceNow'],
        ['single-table-step2-entity-finder', 'Python 3.12', '10 GB', '15 min', 'AI entity matching'],
        ['single-table-step3-relationships', 'Python 3.12', '512 MB', '10 min', 'Discover relationships'],
        ['single-table-step4-health', 'Python 3.11', '512 MB', '10 min', 'Health monitoring'],
        ['single-table-step5-visualization', 'Python 3.11', '1 GB', '15 min', 'Generate visualizations'],
      ]}
    />

    <Callout type="info" title="Automated Pipeline">
      Each step automatically triggers the next via async Lambda invocation. 
      The pipeline can be scheduled via EventBridge for hourly runs.
    </Callout>
  </>
);

const SCMStep1Content = () => (
  <>
    <Paragraph>
      Step 1 fetches change requests from ServiceNow, enriches them with project/incident 
      descriptions, and stores them in DynamoDB as the foundation for the pipeline.
    </Paragraph>

    <Heading level={2}>Function Details</Heading>
    <Table 
      headers={['Property', 'Value']}
      rows={[
        ['Function Name', 'single-table-step1-changes'],
        ['Runtime', 'Python 3.11'],
        ['Handler', 'lambda_function.lambda_handler'],
        ['Timeout', '300 seconds (5 minutes)'],
        ['Memory', '416 MB'],
      ]}
    />

    <Heading level={2}>Environment Variables</Heading>
    <CodeBlock 
      filename="Step 1 Configuration"
      language="bash"
      code={`COMPREHENSIVE_TABLE=v-scr-ui-changes
HOURS_LOOKBACK=600
SERVICENOW_USERNAME=<username>
SERVICENOW_PASSWORD=<password>
SERVICENOW_INSTANCE_URL=https://dish.service-now.com
NEXT_LAMBDA=single-table-step2-entity-finder
FIX_RECORD_TYPE=true`}
    />

    <Heading level={2}>Processing Steps</Heading>
    <Steps>
      <Step title="Fetch Metric Instances">
        Queries ServiceNow metric_instance table for change approval state transitions
      </Step>
      <Step title="Organize by Change Number">
        Groups metric instances by change number and extracts sys_ids
      </Step>
      <Step title="Fetch Change Details">
        Batch fetches full change details from u_software_change table (50 per batch)
      </Step>
      <Step title="Enrich with Project/Incident">
        Fetches descriptions from linked project/incident records
      </Step>
      <Step title="Store in DynamoDB">
        Creates or updates records, preserving enrichment from downstream steps
      </Step>
    </Steps>

    <Heading level={2}>Data Structure</Heading>
    <CodeBlock 
      filename="DynamoDB Record"
      language="json"
      code={`{
  "id": "SCR0144933",
  "record_type": "change_state",
  "change_number": "SCR0144933",
  "short_description": "Deploy application",
  "u_version": "1.2.3",
  "u_app_name": "my-app",
  "states": [{"metric_value": "Move to Prod", ...}],
  "current_state": "Move to Prod",
  "scanned": false,
  "match_status": "pending"
}`}
    />

    <Callout type="warning" title="Common Issues">
      <List items={[
        'No metric instances found: Check ServiceNow credentials and HOURS_LOOKBACK',
        'Project descriptions missing: Normal if changes lack linked projects',
        'Records not updating: Verify state actually changed'
      ]} />
    </Callout>
  </>
);

const SCMStep2Content = () => (
  <>
    <Paragraph>
      Step 2 uses AWS Bedrock Knowledge Base to match ServiceNow changes to Dynatrace 
      entities using AI-powered semantic search with parallel processing.
    </Paragraph>

    <Heading level={2}>Function Details</Heading>
    <Table 
      headers={['Property', 'Value']}
      rows={[
        ['Function Name', 'single-table-step2-entity-finder'],
        ['Runtime', 'Python 3.12'],
        ['Handler', 'lambda_function.lambda_handler'],
        ['Timeout', '900 seconds (15 minutes)'],
        ['Memory', '10240 MB (10 GB)'],
      ]}
    />

    <Heading level={2}>Environment Variables</Heading>
    <CodeBlock 
      filename="Step 2 Configuration"
      language="bash"
      code={`COMPREHENSIVE_TABLE=v-scr-ui-changes
BATCH_SIZE=150
BEDROCK_KNOWLEDGE_BASE_ID=CMQNBHW0E6
DYNATRACE_ENVIRONMENT_URL=https://cwv21753.live.dynatrace.com
DYNATRACE_API_TOKEN=<token>
NEXT_LAMBDA=single-table-step3-relationships
MAX_WORKERS=5`}
    />

    <Heading level={2}>AI Matching Process</Heading>
    <Steps>
      <Step title="Fetch Unprocessed Changes">
        Scans DynamoDB for records without step2_status=completed
      </Step>
      <Step title="Parallel Bedrock Matching">
        Uses ThreadPoolExecutor with 5 workers for concurrent processing
      </Step>
      <Step title="Entity Matching Logic">
        Analyzes display name, description, version, platform, and Git URLs
      </Step>
      <Step title="Batch Update DynamoDB">
        Updates all matched records with entity ID, confidence, and reasoning
      </Step>
    </Steps>

    <Heading level={2}>Confidence Scoring</Heading>
    <Table 
      headers={['Level', 'Score', 'Criteria']}
      rows={[
        ['High', '95', 'Exact match on display name or app name'],
        ['Medium', '75', 'Partial match on key fields'],
        ['Low', '50', 'Substring match or single field match'],
        ['No Match', '0', 'No relevant entities found'],
      ]}
    />

    <Heading level={2}>Entity Type Priority</Heading>
    <List items={[
      'SERVICE-* (HIGHEST priority)',
      'APPLICATION-*',
      'PROCESS_GROUP-*',
      'HOST-*',
      'APPLICATION_METHOD_GROUP-* (LOWEST priority)'
    ]} ordered />

    <Callout type="info" title="Bedrock Configuration">
      Uses Claude 3.5 Sonnet v2 model with Knowledge Base retrieval. 
      Returns up to 25 results per query for semantic matching.
    </Callout>
  </>
);

const SCMStep3Content = () => (
  <>
    <Paragraph>
      Step 3 fetches entity relationships from Dynatrace API, discovers upstream and 
      downstream dependencies, and generates graph data for visualization.
    </Paragraph>

    <Heading level={2}>Function Details</Heading>
    <Table 
      headers={['Property', 'Value']}
      rows={[
        ['Function Name', 'single-table-step3-relationships'],
        ['Runtime', 'Python 3.12'],
        ['Handler', 'step3_404_fixed.lambda_handler'],
        ['Timeout', '600 seconds (10 minutes)'],
        ['Memory', '512 MB'],
      ]}
    />

    <Heading level={2}>Environment Variables</Heading>
    <CodeBlock 
      filename="Step 3 Configuration"
      language="bash"
      code={`COMPREHENSIVE_TABLE=v-scr-ui-changes
BATCH_SIZE=50
DYNATRACE_ENVIRONMENT_URL=https://cwv21753.live.dynatrace.com
DYNATRACE_API_TOKEN=<token>
NEXT_LAMBDA=single-table-step4-health
FETCH_DISPLAY_NAMES=true`}
    />

    <Heading level={2}>Relationship Discovery</Heading>
    <Steps>
      <Step title="Fetch Records with Entities">
        Scans for records with step2_entity_id that haven't been processed
      </Step>
      <Step title="Fetch Entity Relationships">
        Calls Dynatrace Entities API v2 for each entity
      </Step>
      <Step title="Process Relationships">
        Categorizes into upstream (dependencies) and downstream (dependents)
      </Step>
      <Step title="Generate Graph Data">
        Creates nodes and edges for visualization
      </Step>
    </Steps>

    <Heading level={2}>Relationship Types</Heading>
    <Table 
      headers={['Direction', 'Type', 'Description']}
      rows={[
        ['Upstream', 'isServiceOf', 'Service runs on process group'],
        ['Upstream', 'runsOn', 'Process runs on host'],
        ['Downstream', 'calls', 'Service calls another service'],
        ['Downstream', 'callsService', 'Application calls service'],
      ]}
    />

    <Heading level={2}>Graph Data Structure</Heading>
    <CodeBlock 
      filename="Graph Output"
      language="json"
      code={`{
  "nodes": [
    {"id": "SERVICE-ABC", "name": "my-service", "type": "main", "level": 0},
    {"id": "PROCESS_GROUP-123", "type": "upstream", "level": -1}
  ],
  "edges": [
    {"source": "PROCESS_GROUP-123", "target": "SERVICE-ABC", "type": "isServiceOf"}
  ]
}`}
    />

    <Callout type="warning" title="Common Issues">
      <List items={[
        'No relationships found: Entity might be isolated or check API permissions',
        '404 errors: Entity ID might be stale or deleted from Dynatrace',
        'Timeout issues: Reduce batch size or disable display name fetching'
      ]} />
    </Callout>
  </>
);

const SCMStep4Content = () => (
  <>
    <Paragraph>
      Step 4 performs comprehensive health checks on entities and their relationships 
      using Dynatrace metrics and problems API with parallel processing and rate limiting.
    </Paragraph>

    <Heading level={2}>Function Details</Heading>
    <Table 
      headers={['Property', 'Value']}
      rows={[
        ['Function Name', 'single-table-step4-health'],
        ['Runtime', 'Python 3.11'],
        ['Handler', 'single_table_step4_health.lambda_handler'],
        ['Timeout', '600 seconds (10 minutes)'],
        ['Memory', '512 MB'],
      ]}
    />

    <Heading level={2}>Environment Variables</Heading>
    <CodeBlock 
      filename="Step 4 Configuration"
      language="bash"
      code={`COMPREHENSIVE_TABLE=v-scr-ui-changes
BATCH_SIZE=50
HEALTH_CHECK_WINDOW_HOURS=72
DYNATRACE_ENVIRONMENT_URL=https://cwv21753.live.dynatrace.com
DYNATRACE_API_TOKEN=<token>
NEXT_LAMBDA=single-table-step5-visualization
MAX_WORKERS=5
API_RATE_LIMIT_PER_SECOND=10`}
    />

    <Heading level={2}>Health Check Process</Heading>
    <Steps>
      <Step title="Fetch Records with Relationships">
        Scans for records with relationships and valid entity IDs
      </Step>
      <Step title="Check Main Entity Health">
        Fetches metrics and checks for active problems
      </Step>
      <Step title="Check Relationship Health">
        Parallel health checks for all upstream/downstream entities
      </Step>
      <Step title="Store Health Data">
        Updates DynamoDB with health scores and history
      </Step>
    </Steps>

    <Heading level={2}>Health Thresholds by Entity Type</Heading>
    <Table 
      headers={['Entity Type', 'Healthy', 'Degraded', 'Metric']}
      rows={[
        ['HOST', '≥90% availability', '≥80% availability', 'builtin:host.availability'],
        ['SERVICE', '<2000ms response', '<5000ms response', 'builtin:service.response.time'],
        ['CONTAINER', '<85% CPU', '<95% CPU', 'builtin:containers.cpu.usage'],
      ]}
    />

    <Heading level={2}>Overall Health Calculation</Heading>
    <List items={[
      'ALL_HEALTHY: 100% of entities healthy',
      'MOSTLY_HEALTHY: >50% healthy, <40% unhealthy',
      'DEGRADED: 40-60% unhealthy',
      'CRITICAL: >60% unhealthy'
    ]} />

    <Callout type="info" title="Rate Limiting">
      Uses token bucket algorithm with 10 requests/second default. 
      Implements exponential backoff with max 3 retries.
    </Callout>
  </>
);

const SCMStep5Content = () => (
  <>
    <Paragraph>
      Step 5 generates comprehensive network diagrams and impact analysis from relationship 
      and health data, storing large visualization JSON files in S3 with compression.
    </Paragraph>

    <Heading level={2}>Function Details</Heading>
    <Table 
      headers={['Property', 'Value']}
      rows={[
        ['Function Name', 'single-table-step5-visualization'],
        ['Runtime', 'Python 3.11'],
        ['Handler', 'fixed_step5_visualization.lambda_handler'],
        ['Timeout', '900 seconds (15 minutes)'],
        ['Memory', '1024 MB'],
      ]}
    />

    <Heading level={2}>Environment Variables</Heading>
    <CodeBlock 
      filename="Step 5 Configuration"
      language="bash"
      code={`COMPREHENSIVE_TABLE=v-scr-ui-changes
BATCH_SIZE=100
NEXT_LAMBDA=single-table-step1-changes
FORCE_REPROCESS=false`}
    />

    <Heading level={2}>S3 Storage</Heading>
    <Table 
      headers={['Property', 'Value']}
      rows={[
        ['Bucket', 'ui-scr-visual-network-diagrams'],
        ['Path', 'step5-visualizations/{change_number}/{timestamp}_visualization.json.gz'],
        ['Compression', 'gzip (70-90% reduction)'],
        ['Encryption', 'AES256 server-side'],
      ]}
    />

    <Heading level={2}>Visualization Generation</Heading>
    <Steps>
      <Step title="Fetch Records">
        Scans for records with health_processed=true and relationships
      </Step>
      <Step title="Generate Visualization">
        Creates metadata, graph, timeline, and impact analysis
      </Step>
      <Step title="Upload to S3">
        Compresses and uploads with metadata tags
      </Step>
      <Step title="Update DynamoDB">
        Stores S3 reference and summary (handles 400KB limit)
      </Step>
    </Steps>

    <Heading level={2}>Impact Scoring</Heading>
    <Table 
      headers={['Level', 'Unhealthy %', 'Description']}
      rows={[
        ['LOW', '0-30%', 'Minimal impact on dependencies'],
        ['MEDIUM', '30-60%', 'Moderate impact requiring attention'],
        ['HIGH', '60-100%', 'Critical impact on infrastructure'],
      ]}
    />

    <Heading level={2}>Visualization Structure</Heading>
    <CodeBlock 
      filename="Visualization Output"
      language="json"
      code={`{
  "metadata": {"change_number": "SCR0144933", "current_state": "Move to Prod"},
  "overall": {"status": "MOSTLY_HEALTHY", "risk": "LOW"},
  "impact": {"level": "LOW", "score": 13.3, "total_entities": 15},
  "graph": {"nodes": [...], "edges": [...], "node_count": 15},
  "timeline": {"events": [...], "event_count": 5},
  "health_summary": {"main_entity": {...}, "relationships": {...}}
}`}
    />

    <Callout type="success" title="Pipeline Cycle">
      After Step 5 completes, it triggers Step 1 again to restart the pipeline cycle, 
      enabling continuous monitoring of changes.
    </Callout>
  </>
);

const SCMDeploymentContent = () => (
  <>
    <Paragraph>
      Complete guide for deploying and configuring the ServiceNow Change Management Pipeline.
    </Paragraph>

    <Heading level={2}>Prerequisites</Heading>
    <List items={[
      'AWS Account with appropriate permissions',
      'ServiceNow instance with API access',
      'Dynatrace environment with API token',
      'AWS Bedrock Knowledge Base (for Step 2)'
    ]} />

    <Heading level={2}>Infrastructure Setup</Heading>
    
    <Heading level={3}>1. Create DynamoDB Table</Heading>
    <CodeBlock 
      filename="Terminal"
      language="bash"
      code={`aws dynamodb create-table \\
  --table-name v-scr-ui-changes \\
  --attribute-definitions AttributeName=id,AttributeType=S \\
  --key-schema AttributeName=id,KeyType=HASH \\
  --billing-mode PAY_PER_REQUEST \\
  --region us-west-2`}
    />

    <Heading level={3}>2. Create S3 Bucket</Heading>
    <CodeBlock 
      filename="Terminal"
      language="bash"
      code={`aws s3 mb s3://ui-scr-visual-network-diagrams --region us-west-2

aws s3api put-bucket-encryption \\
  --bucket ui-scr-visual-network-diagrams \\
  --server-side-encryption-configuration '{
    "Rules": [{"ApplyServerSideEncryptionByDefault": {"SSEAlgorithm": "AES256"}}]
  }'`}
    />

    <Heading level={2}>IAM Permissions</Heading>
    <CodeBlock 
      filename="Required Permissions"
      language="json"
      code={`{
  "DynamoDB": ["GetItem", "PutItem", "UpdateItem", "Scan", "Query"],
  "Lambda": ["InvokeFunction"],
  "Bedrock": ["RetrieveAndGenerate", "Retrieve"],
  "S3": ["PutObject", "GetObject"],
  "CloudWatch": ["PutMetricData"]
}`}
    />

    <Heading level={2}>EventBridge Schedule (Optional)</Heading>
    <CodeBlock 
      filename="Terminal"
      language="bash"
      code={`aws events put-rule \\
  --name servicenow-pipeline-schedule \\
  --schedule-expression "rate(1 hour)" \\
  --region us-west-2`}
    />

    <Callout type="warning" title="Security">
      Store ServiceNow credentials in AWS Secrets Manager. Rotate Dynatrace API tokens 
      regularly. Use least-privilege IAM policies in production.
    </Callout>
  </>
);

const SCMTroubleshootingContent = () => (
  <>
    <Heading level={2}>Pipeline Not Running</Heading>
    <List items={[
      'Check EventBridge rule is enabled',
      'Verify Lambda permissions',
      'Check CloudWatch logs for errors',
      'Verify environment variables'
    ]} />

    <Heading level={2}>Step 1 Issues</Heading>
    <Table 
      headers={['Issue', 'Cause', 'Solution']}
      rows={[
        ['No changes fetched', 'ServiceNow credentials', 'Check username/password env vars'],
        ['Missing descriptions', 'No linked projects', 'Normal behavior'],
        ['Records not updating', 'State unchanged', 'Verify state actually changed'],
      ]}
    />

    <Heading level={2}>Step 2 Issues</Heading>
    <Table 
      headers={['Issue', 'Cause', 'Solution']}
      rows={[
        ['Low match rate', 'Knowledge Base content', 'Review KB data quality'],
        ['Bedrock timeouts', 'Large batch size', 'Reduce BATCH_SIZE'],
        ['Memory issues', 'Parallel processing', 'Increase Lambda memory'],
      ]}
    />

    <Heading level={2}>Step 3 Issues</Heading>
    <Table 
      headers={['Issue', 'Cause', 'Solution']}
      rows={[
        ['No relationships', 'Isolated entity', 'Check entity type'],
        ['404 errors', 'Stale entity ID', 'Entity deleted from Dynatrace'],
        ['Timeout', 'Too many entities', 'Reduce batch size'],
      ]}
    />

    <Heading level={2}>Step 4 Issues</Heading>
    <Table 
      headers={['Issue', 'Cause', 'Solution']}
      rows={[
        ['High unhealthy count', 'Threshold config', 'Adjust health thresholds'],
        ['Rate limiting', 'Too many API calls', 'Reduce API_RATE_LIMIT'],
        ['Timeout', 'Large entity count', 'Reduce BATCH_SIZE'],
      ]}
    />

    <Heading level={2}>Step 5 Issues</Heading>
    <Table 
      headers={['Issue', 'Cause', 'Solution']}
      rows={[
        ['Item size exceeded', 'Large visualization', 'Auto-fallback to minimal summary'],
        ['S3 upload fails', 'Permissions', 'Check IAM role'],
        ['Missing data', 'health_processed=false', 'Verify Step 4 completed'],
      ]}
    />

    <Heading level={2}>Debug Commands</Heading>
    <CodeBlock 
      filename="Terminal"
      language="bash"
      code={`# View Step 1 logs
aws logs tail /aws/lambda/single-table-step1-changes --follow

# Check DynamoDB record
aws dynamodb get-item --table-name v-scr-ui-changes \\
  --key '{"id":{"S":"SCR0144933"}}'

# List S3 visualizations
aws s3 ls s3://ui-scr-visual-network-diagrams/step5-visualizations/`}
    />
  </>
);

const SCMPerformanceContent = () => (
  <>
    <Heading level={2}>Typical Processing Times</Heading>
    <Table 
      headers={['Step', 'Time', 'Records']}
      rows={[
        ['Step 1', '30-60 seconds', '50 changes'],
        ['Step 2', '2-5 minutes', '150 changes (parallel)'],
        ['Step 3', '1-3 minutes', '50 changes'],
        ['Step 4', '2-4 minutes', '25 changes'],
        ['Step 5', '1-2 minutes', '100 changes'],
      ]}
    />

    <Heading level={2}>Throughput</Heading>
    <List items={[
      'End-to-end: ~10-15 minutes for 50 changes',
      'Daily capacity: 3,000-5,000 changes (with hourly runs)',
      'Scalability: Adjust batch sizes and concurrency'
    ]} />

    <Heading level={2}>Batch Size Optimization</Heading>
    <Table 
      headers={['Step', 'Recommended', 'Notes']}
      rows={[
        ['Step 1', '50-100', 'Limited by ServiceNow API'],
        ['Step 2', '100-200', 'Limited by Bedrock rate limits'],
        ['Step 3', '25-75', 'Depends on relationship complexity'],
        ['Step 4', '25-50', 'Depends on entity count'],
        ['Step 5', '50-150', 'Depends on visualization size'],
      ]}
    />

    <Heading level={2}>Cost Estimates (Monthly)</Heading>
    <Table 
      headers={['Service', 'Cost Range', 'Notes']}
      rows={[
        ['Lambda', '$20-50', 'Depends on execution frequency'],
        ['DynamoDB', '$10-30', 'On-demand, ~10K items'],
        ['S3', '$5-15', 'Storage + requests'],
        ['Bedrock', '$50-200', 'Depends on match volume'],
        ['Total', '$90-305', 'Typical workload'],
      ]}
    />

    <Callout type="info" title="Cost Optimization">
      Use on-demand DynamoDB for variable workloads. Enable S3 lifecycle policies 
      for old visualizations. Set CloudWatch log retention to 7-14 days.
    </Callout>
  </>
);

// ============================================================================
// DOCS CONFIGURATION
// ============================================================================

const docsConfig = {
  title: 'VISION Knowledge Base',
  description: 'Technical documentation, architecture guides, and troubleshooting resources',
  defaultPage: null,
  items: [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Book,
      description: 'Quick start guide and prerequisites',
      content: <GettingStartedContent />
    },
    {
      id: 'architecture',
      title: 'Architecture',
      icon: Layers,
      children: [
        {
          id: 'arch-overview',
          title: 'System Overview',
          content: <ArchitectureContent />
        },
        {
          id: 'arch-dynamodb',
          title: 'DynamoDB Schema',
          content: <DynamoDBContent />
        },
        {
          id: 'arch-bedrock',
          title: 'Bedrock Integration',
          content: <BedrockContent />
        }
      ]
    },
    {
      id: 'authentication',
      title: 'Authentication',
      icon: Shield,
      children: [
        {
          id: 'auth-overview',
          title: 'Auth Overview',
          content: <AuthenticationContent />
        },
        {
          id: 'auth-groups',
          title: 'Groups & Permissions',
          content: <GroupsContent />
        },
        {
          id: 'auth-lambda',
          title: 'Lambda Authorizer',
          content: <LambdaAuthorizerContent />
        }
      ]
    },
    {
      id: 'deployment',
      title: 'Deployment',
      icon: Terminal,
      children: [
        {
          id: 'deploy-guide',
          title: 'Deployment Guide',
          content: <DeploymentContent />
        },
        {
          id: 'deploy-api',
          title: 'API Endpoints',
          content: <APIEndpointsContent />
        }
      ]
    },
    {
      id: 'aws-resources',
      title: 'AWS Resources',
      icon: Server,
      children: [
        {
          id: 'iam-roles',
          title: 'IAM Roles & Policies',
          content: <IAMRolesContent />
        },
        {
          id: 'lambda-functions',
          title: 'Lambda Functions',
          content: <LambdaFunctionsContent />
        },
        {
          id: 'dynamodb-tables',
          title: 'DynamoDB Tables',
          content: <DynamoDBTablesContent />
        }
      ]
    },
    {
      id: 'scm-pipeline',
      title: 'SCM Pipeline',
      icon: GitBranch,
      children: [
        {
          id: 'scm-overview',
          title: 'Pipeline Overview',
          content: <SCMPipelineOverviewContent />
        },
        {
          id: 'scm-step1',
          title: 'Step 1: Changes Fetcher',
          content: <SCMStep1Content />
        },
        {
          id: 'scm-step2',
          title: 'Step 2: Entity Finder',
          content: <SCMStep2Content />
        },
        {
          id: 'scm-step3',
          title: 'Step 3: Relationships',
          content: <SCMStep3Content />
        },
        {
          id: 'scm-step4',
          title: 'Step 4: Health Check',
          content: <SCMStep4Content />
        },
        {
          id: 'scm-step5',
          title: 'Step 5: Visualization',
          content: <SCMStep5Content />
        },
        {
          id: 'scm-deployment',
          title: 'Deployment Guide',
          content: <SCMDeploymentContent />
        },
        {
          id: 'scm-troubleshooting',
          title: 'Troubleshooting',
          content: <SCMTroubleshootingContent />
        },
        {
          id: 'scm-performance',
          title: 'Performance & Costs',
          content: <SCMPerformanceContent />
        }
      ]
    },

    {
      id: 'auth-docs',
      title: 'Auth Docs',
      icon: Lock,
      children: [
        {
          id: 'auth-docs-overview',
          title: 'Overview',
          content: <AuthOverviewContent />
        },
        {
          id: 'auth-docs-flow',
          title: 'Authentication Flow',
          content: <AuthFlowContent />
        },
        {
          id: 'auth-docs-environments',
          title: 'Environment Config',
          content: <AuthEnvironmentsContent />
        },
        {
          id: 'auth-docs-groups',
          title: 'Groups & Permissions',
          content: <AuthGroupsPermissionsContent />
        },
        {
          id: 'auth-docs-authorizer',
          title: 'Lambda Authorizer',
          content: <AuthLambdaAuthorizerContent />
        },
        {
          id: 'auth-docs-okta',
          title: 'Okta Configuration',
          content: <AuthOktaConfigContent />
        },
        {
          id: 'auth-docs-troubleshooting',
          title: 'Troubleshooting',
          content: <AuthTroubleshootingContent />
        },
        {
          id: 'auth-docs-files',
          title: 'Key Files',
          content: <AuthKeyFilesContent />
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: AlertTriangle,
      description: 'Common issues and solutions',
      content: <TroubleshootingContent />
    }
  ]
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const KnowledgeBaseDocs = () => {
  return (
    <div className="h-full relative">
      {/* Background effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-accent opacity-[0.05] blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-accent opacity-[0.04] blur-[150px] rounded-full pointer-events-none" />
      
      <DocsLayout docsConfig={docsConfig} />
    </div>
  );
};

export default KnowledgeBaseDocs;
